import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  approvalWithdraw,
  getDetailDriver,
  getDetailWithdraw,
  getTransactionHistory,
  getVehiclePackages,
  uploadTransferProofWithdraw,
} from 'features/drivers/actions';
import { checkEmptyObject, setCurrency } from 'utils/functionality';
import { trackEvent } from 'utils/analytics';
import { useAppContext } from 'components/Context/AppContext';
import { Button, InputField, PaginationTable, TableWrapper } from 'components/Global';
import PreviewImage from 'components/Modals/PreviewImage';
import { ReactComponent as LeftArrow } from 'icons/left-arrow.svg';
import { ReactComponent as DriverIcon } from 'icons/driving-wheel-icon.svg';
import { ReactComponent as DownloadIcon } from 'icons/download-image-blue-icon.svg';
import { format } from 'date-fns';
import ImageViewer from 'components/Global/ImageViewer';
import UploadImageWithdraw from 'components/Global/UploadImageWithdraw';
import { getConfig } from 'config/runtime';

const IMAGE_BASE_URL = getConfig('REACT_APP_IMAGES');

const DetailWithdraw = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    setShowWithdrawFeeModal,
    setShowSpinner,
    setShowConfirmation,
    // setShowTransferWithdrawFee,
    setShowRejectWithdrawFee,
    showToast,
  } = useAppContext();

  const {
    selected: selectedWithdrawData,
    transactionHistory: transactionHistoryData,
    isLoading: withDrawLoading,
  } = useSelector((state) => state.withdraw);
  const { selected: selectedDriversData, isLoading: driversLoading } = useSelector((state) => state.drivers);

  const [showImage, setShowImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState({
    url: '',
    image: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  const backHandler = () => {
    navigate(-1);
  };

  const imagePreviewHandler = (image) => {
    setShowImage(true);
    setSelectedImage((prev) => ({ ...prev, url: image }));
  };

  const withdrawFeeHandler = () => {
    setShowWithdrawFeeModal(true);
  };

  const rejectWithdrawFeeHandler = () => {
    setShowConfirmation({
      show: true,
      message: 'Apakah anda ingin menolak permintaan Withdraw ini?',
      onClick: async () => {
        setShowRejectWithdrawFee(true);
      },
    });
  };

  const confirmationWithdrawFeeHandler = () => {
    setShowConfirmation({
      show: true,
      message: 'Apakah anda ingin konfirmasi penyelesaian Transfer Manual penarikan dana?',
      onClick: async () => {
        try {
          trackEvent('driver_withdraw_decided', {
            withdraw_id: id,
            decision: 'approve',
            source: 'driver_withdraw_detail',
          });
          await dispatch(approvalWithdraw({ id, status: 'approve' })).unwrap();
          showToast({ type: 'success', message: 'Berhasil Proses Withdraw' });
          dispatch(getDetailWithdraw(id));
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error);
          showToast({ type: 'error', message: 'Terjadi Kesalahan' });
        }
      },
    });
  };

  const downloadTransferProof = (image) => {
    const imageUrl = `${IMAGE_BASE_URL}${image}`;
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = 'downloaded-image.jpg';
    link.click();
    link.remove();
  };

  const gotTotalCurrency = (saldo, fee, currency, status) => {
    if (status !== 'COMPLETED' && status !== 'REJECTED') {
      if (currency === 'IDR') {
        return setCurrency(saldo + fee)?.replace('Rp', currency === null ? '' : currency);
      }
      if (currency === 'SGD') {
        return `${currency} ${(saldo + fee).toFixed(2)}`;
      }
    } else if (status === 'REJECTED') {
      if (currency === 'IDR') {
        return setCurrency(saldo)?.replace('Rp', currency === null ? '' : currency);
      }
      if (currency === 'SGD') {
        return `${currency} ${saldo.toFixed(2)}`;
      }
    } else {
      if (currency === 'IDR') {
        return setCurrency(saldo)?.replace('Rp', currency === null ? '' : currency);
      }
      if (currency === 'SGD') {
        if (saldo === 0) {
          return `${currency} 0`;
        } else {
          return `${currency} ${saldo.toFixed(2)}`;
        }
      }
    }
  };

  const gotFeeCurrency = (fee, currency) => {
    const RightFee =
      fee && currency === 'IDR'
        ? setCurrency(fee).replace('Rp', `${currency}`)
        : fee && currency === 'SGD'
        ? `${currency} ${fee.toFixed(2)}`
        : fee === 0
        ? `${currency} 0`
        : '-';
    return RightFee;
  };

  const handleSelectImage = (image) => {
    setSelectedImage((prev) => ({ ...prev, image: image }));
  };

  const handleRemoveImage = () => {
    setSelectedImage((prev) => ({ ...prev, image: null }));
  };

  const handleUploadImage = async () => {
    if (!selectedImage.image) {
      return showToast({ type: 'error', message: 'Upload Bukti Transfer' });
    }

    try {
      await dispatch(uploadTransferProofWithdraw({ image: selectedImage.image, id })).unwrap();
      dispatch(getDetailWithdraw(id));
      showToast({ type: 'success', message: 'Berhasil Upload Foto' });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      showToast({ type: 'error', message: 'Gagal Upload Foto' });
    }
  };

  useEffect(() => {
    dispatch(getDetailWithdraw(id));
  }, []);

  useEffect(() => {
    if (!selectedWithdrawData || checkEmptyObject(selectedWithdrawData)) return;
    dispatch(getDetailDriver(selectedWithdrawData.withdraw?.request_by_id));
    dispatch(
      getTransactionHistory({
        id: selectedWithdrawData.withdraw?.request_by_id,
        showType: 'wd_admin',
        refId: id,
        limit: 10,
        page: currentPage,
      }),
    );
  }, [selectedWithdrawData, currentPage]);

  useEffect(() => {
    if (!selectedDriversData || checkEmptyObject(selectedDriversData)) return;

    const locationId = selectedDriversData?.user_location?.location_id;
    dispatch(getVehiclePackages(locationId));
  }, [selectedDriversData]);

  useEffect(() => {
    if (withDrawLoading || driversLoading) {
      setShowSpinner(true);
    } else {
      setShowSpinner(false);
    }
  }, [withDrawLoading, driversLoading]);

  return (
    <>
      <div className="withdraw">
        <header className="withdraw__header" onClick={backHandler}>
          <LeftArrow />
          <p>Kembali</p>
        </header>

        <TableWrapper icon={<DriverIcon fill="#009EF7" width="25px" height="25px" />} title="Driver">
          <h1 style={{ color: '#009EF7', fontSize: 16, marginBottom: 16 }}>Detail Withdraw</h1>

          <div>
            <h2>Saldo Driver</h2>
            <div className="withdraw__saldo-detail">
              <p>Jumlah Saldo</p>
              <p>
                {gotTotalCurrency(
                  selectedWithdrawData?.available_amount,
                  selectedWithdrawData?.withdraw?.amount,
                  selectedWithdrawData?.currency,
                  selectedWithdrawData?.withdraw?.status,
                ) || '0'}
              </p>
            </div>
          </div>

          <div className="withdraw__driver-detail">
            <InputField
              label="Nomor Rekening"
              disable
              readOnly
              value={selectedDriversData?.account_bank?.no_rek || '-'}
            />
            <InputField
              label="Nama Pemilik Rekening"
              disable
              readOnly
              value={selectedDriversData?.account_bank?.nama_rek || '-'}
            />
            <InputField
              label="Bank Asal Rekening"
              disable
              readOnly
              value={selectedDriversData?.account_bank?.nama_bank || '-'}
            />
          </div>

          <div>
            <h2>Detail Penarikan</h2>
            <div className="withdraw__fee-detail">
              <div>
                <p>Jumlah Permintaan Nominal Withdraw</p>
                <p>{gotFeeCurrency(selectedWithdrawData?.withdraw?.amount, selectedWithdrawData?.currency) || '-'}</p>
              </div>

              <div
                className="transfer-proof-download"
                onClick={
                  selectedWithdrawData?.withdraw?.proof_of_transfer
                    ? () => downloadTransferProof(selectedWithdrawData?.withdraw?.proof_of_transfer)
                    : null
                }
              >
                {selectedWithdrawData?.withdraw?.status !== 'REQUEST' &&
                selectedWithdrawData?.withdraw?.status !== 'REJECTED' &&
                selectedWithdrawData?.withdraw?.proof_of_transfer ? (
                  <>
                    <DownloadIcon />
                    <p>Download Bukti Transfer</p>
                  </>
                ) : null}
              </div>
            </div>

            {selectedWithdrawData?.withdraw?.status !== 'REQUEST' &&
            selectedWithdrawData?.withdraw?.status !== 'REJECTED' ? (
              <div className="withdraw__proof-of-transfer">
                {selectedWithdrawData?.withdraw?.proof_of_transfer ? (
                  <ImageViewer
                    onClick={imagePreviewHandler}
                    value={
                      selectedWithdrawData?.withdraw?.proof_of_transfer
                        ? IMAGE_BASE_URL + selectedWithdrawData?.withdraw?.proof_of_transfer
                        : ''
                    }
                  />
                ) : (
                  <div>
                    <h3 style={{ fontSize: 12, marginBottom: 14 }}>Bukti Transfer</h3>
                    <UploadImageWithdraw
                      handleImageChange={handleSelectImage}
                      htmlFor="upload-withdraw-transfer-proof"
                      handlePreview={imagePreviewHandler}
                      handleRemove={handleRemoveImage}
                      handleUpload={handleUploadImage}
                      imageFile={selectedImage.image}
                    />
                  </div>
                )}
              </div>
            ) : null}

            <div className="action-button">
              {selectedWithdrawData?.withdraw?.status !== 'COMPLETED' &&
                selectedWithdrawData?.withdraw?.status !== 'REJECTED' && (
                  <Button className="button-withdraw cancel" onClick={rejectWithdrawFeeHandler}>
                    Tolak Permintaan
                  </Button>
                )}

              {selectedWithdrawData?.withdraw?.status === 'REQUEST' && (
                <Button className="button-withdraw" onClick={withdrawFeeHandler}>
                  Proses Withdraw
                </Button>
              )}

              {selectedWithdrawData?.withdraw?.status === 'PROCESSED' && (
                <Button className="button-withdraw confirmation" onClick={confirmationWithdrawFeeHandler}>
                  Konfirmasi Sudah Diproses
                </Button>
              )}
            </div>
          </div>
        </TableWrapper>

        <div className="withdraw__saldo-wrapper">
          <h2>History Transaksi</h2>
          <ul className="withdraw__transaction">
            {checkEmptyObject(transactionHistoryData) ||
            !transactionHistoryData.data ||
            transactionHistoryData.data.length === 0 ? (
              <p className="empty-data">Belum ada Transaksi</p>
            ) : (
              transactionHistoryData.data.map((item) => (
                <li key={item.id}>
                  <div>
                    <p>{item.description}</p>
                    <p>
                      {format(new Date(item.date), 'dd MMMM yyyy')} {format(new Date(item.date), 'HH:mm:ss')}
                    </p>
                  </div>
                  <p className={clsx('amount', item.amount < 0 ? 'minus' : 'plus')}>
                    {item.amount < 0
                      ? `- ${gotFeeCurrency(item.amount, item.currency).replace('-', '')}`
                      : `+ ${gotFeeCurrency(item.amount, item.currency).replace('+', '')}`}
                  </p>
                </li>
              ))
            )}
          </ul>
          <PaginationTable
            currentPage={currentPage}
            onPageChange={(newPage) => setCurrentPage(newPage)}
            totalCount={transactionHistoryData?.pagination?.total}
          />
        </div>
      </div>
      {showImage &&
        ReactDOM.createPortal(
          <PreviewImage image={selectedImage.url} setShow={() => setShowImage(false)} />,
          document.getElementById('modal'),
        )}
    </>
  );
};

export default DetailWithdraw;
